import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportHorizontalBarChart,
  ReportMultiSeriesChart,
  OUTCOME_COLOURS,
  CHART_COLOURS
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {humanise} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * LEAD1 — Lead Funnel.
 *
 * Deliberately NOT drawn as a funnel widget. This is a snapshot of where leads
 * stand now, not a cohort flowing through stages, so counts do not decrease down
 * the pipeline — Assessment Booked is larger than Web Enquiry because leads
 * accumulate there. Ordered horizontal bars coloured by outcome tell the truth;
 * a funnel would imply a flow that never happened.
 */

const SLUG = 'lead-funnel';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const OUTCOME_LABEL: Record<string, string> = {
  in_progress: 'In progress',
  at_risk: 'At risk',
  won: 'Won',
  lost: 'Lost'
};

const LeadFunnel = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Lead Funnel');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byStatus = collections?.series?.by_status || [];
  const byPeriod = collections?.series?.by_period || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {
      name: 'Subject',
      width: '130px',
      cell: (row: any) =>
        row.subject ? <span className="badge badge-info">{row.subject}</span> : '---'
    },
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Enquired', selector: (row: any) => row.enquired_on || '---', width: '120px'},
    {
      name: 'Current status',
      wrap: true,
      // Same colours as the lead lists (lead/prospective-student/ListProspectiveStudent.tsx):
      // enrolled green, lost red, every stage in between amber.
      cell: (row: any) =>
        row.status ? (
          <span
            className={`badge badge-${
              row.status === 'Enrolled and Paid' ? 'success'
                : row.status === 'Lost' ? 'danger' : 'warning'
            }`}>
            {humanise(row.status)}
          </span>
        ) : (
          '---'
        )
    },
    {name: 'Updated', selector: (row: any) => row.status_updated || '---', width: '120px'},
    {
      name: 'Days in stage',
      width: '130px',
      right: true,
      cell: (row: any) =>
        row.days_in_stage === null || row.days_in_stage === undefined ? (
          '---'
        ) : (
          <span className={row.days_in_stage > 30 ? 'text-danger font-weight-bold' : ''}>
            {row.days_in_stage}
          </span>
        )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Lead Funnel'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Lead Funnel</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, leadStudent: true, leadStatus: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Where leads stand now"
                subtitle="In pipeline order. This is a snapshot, not a flow — later stages can hold more leads than earlier ones."
                loading={loading}
                isEmpty={!byStatus.length}
                height={400}>
                <>
                  <ReportHorizontalBarChart
                    data={byStatus}
                    labelKey="status"
                    valueKey="leads"
                    currency={false}
                    height={360}
                    colourField="outcome"
                    colourMap={OUTCOME_COLOURS}
                  />
                  <div className="d-flex flex-wrap justify-content-center mt-2">
                    {Object.entries(OUTCOME_LABEL).map(([key, label]) => (
                      <span key={key} className="mr-3" style={{fontSize: 11}}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            marginRight: 4,
                            background: OUTCOME_COLOURS[key]
                          }}
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                </>
              </ChartCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Enquiries and enrolments over time"
                subtitle="New enquiries against how many of them converted"
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportMultiSeriesChart
                  data={byPeriod}
                  xKey="bucket"
                  currency={false}
                  series={[
                    {key: 'leads', name: 'Enquiries', type: 'bar', colour: CHART_COLOURS[1]},
                    {key: 'enrolled', name: 'Enrolled', type: 'line', colour: CHART_COLOURS[5]}
                  ]}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Leads</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">No leads match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadFunnel;
